import { useEffect, useState } from 'react';
import useAuthStore from '@/store/useAuthStore';
import { apiFetch } from '@/api/core/apiFetch';

interface DealerNotificationsCount {
  affiliationRequests: number;
  chapterRequests: number;
}

/**
 * useDealerNotifications
 *
 * Polls pending dealer notification counts every 30s.
 * Only active when the authenticated user has user_type === 'dealer'.
 * Canonical path: import { useDealerNotifications } from '@/hooks/domain/useDealerNotifications';
 */
export function useDealerNotifications(): DealerNotificationsCount {
  const { user } = useAuthStore();
  const [affiliationRequests, setAffiliationRequests] = useState(0);
  const [chapterRequests, setChapterRequests] = useState(0);

  const isDealer = user?.user_type === 'dealer';

  useEffect(() => {
    if (!isDealer) return;

    let mounted = true;

    const loadCounts = async () => {
      try {
        const affiliationsRes: any = await apiFetch('/api/dealer-affiliations/requests?status=pending');
        if (!mounted) return;
        setAffiliationRequests(affiliationsRes?.requests?.length || 0);

        // Chapter partnership requests: in attesa di implementazione lato server.
        // Una volta completata la feature chapter join requests, questo verrà
        // sostituito con una chiamata API reale.
        setChapterRequests(0);
      } catch (error) {
        console.error('Error loading dealer notifications:', error);
      }
    };

    loadCounts();
    const interval = setInterval(loadCounts, 30000);

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [isDealer, user?.id]);

  return {
    affiliationRequests,
    chapterRequests,
  };
}
